import { useRef } from "react"

import { useTranslation } from "react-i18next"

import type { VoiceTranscriptEntry } from "../../utils/voice-transcript"

import { ChevronDown, History, Loader2 } from "@/components/icons"
import { NodeConnector, NodePill } from "@/components/ui/node-pill"
import { cn } from "@/lib/utils"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import { VoiceMorph } from "./voice-morph"
import { useLingering, VOICE_GUIDING_MS } from "./voice-morph-utils"
import { splitActivityText } from "./voice-stage-utils"
import styles from "./voice-stage.module.css"
import { getActivityIcon } from "../activity-message/activity-message.utils"

type WorkEntry = Extract<VoiceTranscriptEntry, { kind: "work" }>

type VoiceWorkRowProps = {
  /** Every work entry of the session, oldest first. */
  items: WorkEntry[]
  /** Work is in flight even if no entry says so: a detached job, or a tool that is not a step. */
  working: boolean
  /** What to show as the current step while no entry exists yet ("Using web search"). */
  currentLabel?: string
  /** Seconds the current step has been running past its budget, shown on the label. */
  stalledSeconds?: number | null
  open: boolean
  onToggle: () => void
}

/**
 * The work the assistant is doing, as a flow row under the presence: a label
 * node ("Working" while a step runs, "Activity" after) joined to the current
 * step while one runs. Done, the label stands alone and folds out to the
 * steps; the next committed caller turn clears the row. It shows whenever the session has done any work, whatever the
 * presence is doing, which is the whole point on a full-duplex call where the
 * assistant talks while it searches. Expanded, the steps are drawn as the
 * brand's flow inside a box that scrolls if it has to.
 *
 * Everything here moves on Guiding: the row and its connector are drawn in and
 * out, and the label and the step chip roll from one text to the next. A row
 * the caller has dropped keeps its last steps for that one beat, so it leaves
 * with what it was showing instead of emptying first.
 */
export function VoiceWorkRow({
  items,
  working,
  currentLabel,
  open,
  onToggle,
  stalledSeconds = null,
}: VoiceWorkRowProps) {
  const { t } = useTranslation()
  const present = items.length > 0 || working
  // Kept while leaving, so `guide-out` plays on the row that was there. Set in
  // render on purpose: while present, record `items` even when empty. A
  // present-and-empty stretch (working, no steps yet) must not restore the
  // previous turn's steps on linger. A new turn that is working before its
  // first step starts from its own label.
  const lastItemsRef = useRef(items)
  if (present) lastItemsRef.current = items
  const shown = present ? items : lastItemsRef.current

  const running = present && (working || shown.some((item) => item.status === "running"))
  const current = shown.length > 0 ? shown[shown.length - 1] : null
  const currentText = current
    ? splitActivityText(translateActivityMessage(current.activity, t)).title
    : currentLabel
  const lastChipRef = useRef(currentText)
  if (currentText) lastChipRef.current = currentText
  const chipText = currentText ?? lastChipRef.current
  const chipPresent = running && Boolean(currentText)

  const rowVisible = useLingering(present, VOICE_GUIDING_MS)
  const chipVisible = useLingering(chipPresent, VOICE_GUIDING_MS)

  if (!rowVisible) return null

  const label = running ? t("voice_mode.work.working") : t("voice_mode.work.activity")
  const count =
    shown.length === 1
      ? t("voice_mode.work.step_singular")
      : t("voice_mode.work.steps_plural", { count: shown.length })
  const LabelIcon = running ? Loader2 : History

  return (
    <>
      <NodeConnector
        className={cn(styles.conn, styles.connDraw, !present && styles.guideOut)}
        orientation="vertical"
      />
      <div
        className={cn(styles.work, present ? styles.guideIn : styles.guideOut)}
        data-loading={running}
        data-open={open}
        data-testid="voice-work-row"
      >
        <div className={styles.workRow}>
          <div className={styles.workLabel}>
            <button aria-expanded={open} type="button" onClick={onToggle}>
              <NodePill
                active={running}
                className={cn(styles.pill, running && styles.pillPrimary)}
                kind="flow"
              >
                {/* Working ↔ Activity rolls, icon and all. */}
                <VoiceMorph id={label} mode="roll">
                  <LabelIcon
                    aria-hidden="true"
                    className={cn("node-pill__icon", running && styles.spin)}
                  />
                  {label}
                </VoiceMorph>
                {running && stalledSeconds !== null && (
                  <span className={styles.workElapsed} data-testid="voice-work-elapsed">
                    {stalledSeconds} s
                  </span>
                )}
                <ChevronDown aria-hidden="true" className={styles.chev} />
              </NodePill>
            </button>
          </div>
          {chipVisible && chipText && (
            <div className={cn(styles.workCurrent, chipPresent ? styles.guideIn : styles.guideOut)}>
              <span aria-hidden="true" className={styles.workLine} />
              <NodePill className={styles.pill}>
                {/* The step chip rolls to the next task and eases its width. */}
                <VoiceMorph data-testid="voice-work-current" id={chipText} mode="roll">
                  {chipText}
                </VoiceMorph>
              </NodePill>
            </div>
          )}
        </div>
        {open && shown.length > 0 && (
          <div className={styles.workBox}>
            <div className={styles.workMeta}>{count}</div>
            <ol className={styles.flow}>
              {shown.map((item) => (
                <li key={item.id} className={cn(styles.step, styles.guideIn)}>
                  <NodePill
                    active={item.status === "running"}
                    kind="flow"
                    className={cn(
                      styles.pill,
                      item.status === "running" && styles.pillPrimary,
                      (item.status === "failed" || item.status === "aborted") && styles.stepFailed
                    )}
                  >
                    {item.status === "running" ? (
                      <Loader2 aria-hidden="true" className={cn("node-pill__icon", styles.spin)} />
                    ) : (
                      <span aria-hidden="true" className="node-pill__icon">
                        {getActivityIcon(item.activity)}
                      </span>
                    )}
                    <span>
                      {splitActivityText(translateActivityMessage(item.activity, t)).title}
                    </span>
                  </NodePill>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </>
  )
}
